import React, { useState, memo } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Card,
  message,
  Popconfirm,
  Typography,
  Divider,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService from '../services/api';
import { isAdminOrDC, isAdminOrDCOrTeamLeader } from '../utils/roleUtils';
import { formatUtcApiDateTime } from '../utils/formatDateTime';

const { TextArea } = Input;
const { Text, Title } = Typography;

const DEFAULT_NOTE_IMPORTANCE = 'normal';
const DEFAULT_NOTE_VISIBILITY = 'private';

// Memoized note component to prevent unnecessary re-renders
const MemoizedNote = memo(({ note, onEdit, onDelete, onHighlight, canEdit, canHighlight }: {
  note: LeadNote;
  onEdit: (note: LeadNote) => void;
  onDelete: (noteId: number) => void;
  onHighlight: (noteId: number, highlight: boolean) => void;
  canEdit: (note: LeadNote) => boolean;
  canHighlight: (note: LeadNote) => boolean;
}) => (
    <Card
      size="small"
      style={{
        marginBottom: '8px',
        border: note.is_highlighted ? '2px solid #faad14' : '1px solid rgba(15, 23, 42, 0.1)',
        minHeight: '120px',
        background: '#ffffff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" wrap={false} gutter={8}>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Space>
              <Text strong>{note.title}</Text>
              {note.is_highlighted && (
                <StarFilled style={{ color: '#faad14' }} />
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {canHighlight(note) && (
                <Tooltip title={note.is_highlighted ? 'Remover destaque' : 'Destacar nota'}>
                  <Button
                    type="text"
                    size="small"
                    icon={
                      note.is_highlighted ? (
                        <StarFilled style={{ color: '#faad14' }} />
                      ) : (
                        <StarOutlined />
                      )
                    }
                    onClick={() => onHighlight(note.id, !note.is_highlighted)}
                  />
                </Tooltip>
              )}
              {canEdit(note) && (
                <>
                  <Tooltip title="Editar nota">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => onEdit(note)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Eliminar nota"
                    onConfirm={() => onDelete(note.id)}
                    okText="Sim"
                    cancelText="Não"
                  >
                    <Tooltip title="Eliminar nota">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </>
              )}
            </Space>
          </Col>
        </Row>
        <Text style={{ wordBreak: 'break-word' }}>{note.content}</Text>
        <Space size="small">
          <UserOutlined />
          <Text type="secondary">{note.user_name}</Text>
          <ClockCircleOutlined />
          <Text type="secondary">{formatUtcApiDateTime(note.updated_at)}</Text>
        </Space>
      </Space>
    </Card>
));

interface LeadNote {
  id: number;
  lead_id: number;
  user_id: number;
  title: string;
  content: string;
  importance_level: string;
  is_highlighted: boolean;
  visibility: string;
  created_at: string;
  updated_at: string;
  user_name: string;
}

interface NotesPanelProps {
  visible?: boolean;
  onCancel?: () => void;
  leadId: number;
  notes: LeadNote[];
  onNotesChange: (notes: LeadNote[]) => void;
  onClose?: () => void;
}

const NotesPanel: React.FC<NotesPanelProps> = ({
  visible,
  onCancel,
  leadId,
  notes,
  onNotesChange,
  onClose
}) => {
  const { user } = useCognitoAuth();
  const [form] = Form.useForm();
  const [editingNote, setEditingNote] = useState<LeadNote | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const isModal = visible !== undefined;

  const handleCreateNote = () => {
    setEditingNote(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditNote = (note: LeadNote) => {
    setEditingNote(note);
    form.setFieldsValue({
      title: note.title,
      content: note.content,
    });
    setModalVisible(true);
  };

  const handleSaveNote = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const noteData = {
        title: values.title,
        content: values.content,
        importance_level: DEFAULT_NOTE_IMPORTANCE,
        visibility: DEFAULT_NOTE_VISIBILITY,
      };

      let response: any;
      if (editingNote) {
        response = await apiService.updateLeadNote(leadId, editingNote.id, noteData);
        message.success('Nota atualizada com sucesso!');
      } else {
        response = await apiService.createLeadNote(leadId, noteData);
        message.success('Nota criada com sucesso!');
      }

      // Update notes list
      const updatedNotes = editingNote 
        ? notes.map(n => n.id === editingNote.id ? response.note : n)
        : [...notes, response.note];
      
      onNotesChange(updatedNotes);

      setModalVisible(false);
      setEditingNote(null);
      form.resetFields();

    } catch (error) {
      message.error('Erro ao salvar nota.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await apiService.deleteLeadNote(leadId, noteId);
      const updatedNotes = notes.filter(n => n.id !== noteId);
      onNotesChange(updatedNotes);
      message.success('Nota eliminada com sucesso!');
    } catch (error) {
      message.error('Erro ao eliminar nota.');
    }
  };

  const handleHighlightNote = async (noteId: number, highlight: boolean) => {
    try {
      const response = await apiService.highlightLeadNote(leadId, noteId, { is_highlighted: highlight });
      
      const updatedNotes = notes.map(n => 
        n.id === noteId ? response.note : n
      );
      
      onNotesChange(updatedNotes);
      message.success(`Nota ${highlight ? 'destacada' : 'removida do destaque'} com sucesso!`);
    } catch (error) {
      message.error('Erro ao destacar nota.');
    }
  };

  const canEditNote = (note: LeadNote): boolean => {
    return note.user_id === user?.id || 
           (user && user.role && isAdminOrDC(user.role)) || false;
  };

  const canHighlightNote = (note: LeadNote): boolean => {
    return note.user_id === user?.id || 
           (user && user.role && isAdminOrDCOrTeamLeader(user.role)) || false;
  };

  const highlightedNotes = notes.filter(n => n.is_highlighted);
  const regularNotes = notes.filter(n => !n.is_highlighted);


  const renderContent = () => (
    <div>
      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        }}
      >
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
            Notas do Lead
          </Title>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateNote}
          >
            Nova Nota
          </Button>
        </Col>
      </Row>

      {/* Highlighted Notes */}
      {highlightedNotes.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <Title level={5}>
            <StarFilled style={{ color: '#faad14', marginRight: '8px' }} />
            Notas Destacadas
          </Title>
          {highlightedNotes.map(note => (
            <MemoizedNote
              key={`highlighted-${note.id}`}
              note={note}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              onHighlight={handleHighlightNote}
              canEdit={canEditNote}
              canHighlight={canHighlightNote}
            />
          ))}
        </div>
      )}

      {/* Regular Notes */}
      {regularNotes.length > 0 && (
        <div>
          <Title level={5}>Todas as Notas</Title>
          {regularNotes.map(note => (
            <MemoizedNote
              key={`regular-${note.id}`}
              note={note}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              onHighlight={handleHighlightNote}
              canEdit={canEditNote}
              canHighlight={canHighlightNote}
            />
          ))}
        </div>
      )}

      {notes.length === 0 && (
        <Card
          style={{
            background: '#ffffff',
            border: '1px solid rgba(15, 23, 42, 0.1)',
            borderRadius: 10,
          }}
        >
          <div style={{ textAlign: 'center', padding: '32px' }}>
            <Text type="secondary">Nenhuma nota encontrada</Text>
            <br />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateNote}
              style={{ marginTop: '16px' }}
            >
              Criar Primeira Nota
            </Button>
          </div>
        </Card>
      )}

      {/* Note Form Modal */}
      <Modal
        title={editingNote ? 'Editar Nota' : 'Nova Nota'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingNote(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Título"
            rules={[{ required: true, message: 'Por favor insira um título' }]}
          >
            <Input placeholder="Título da nota" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Conteúdo"
            rules={[{ required: true, message: 'Por favor insira o conteúdo' }]}
          >
            <TextArea rows={4} placeholder="Conteúdo da nota" />
          </Form.Item>

          <Divider />
          
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => {
                setModalVisible(false);
                setEditingNote(null);
                form.resetFields();
              }}
            >
              Cancelar
            </Button>
            <Button 
              type="primary" 
              onClick={handleSaveNote}
              loading={loading}
            >
              {editingNote ? 'Atualizar' : 'Criar'} Nota
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );

  if (isModal) {
    return (
      <Modal
        title="Notas do Lead"
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        {renderContent()}
      </Modal>
    );
  }

  return renderContent();
};

export default NotesPanel; 